import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';

/**
 * Estimate settings (Estimate Tool 10; Phase E4) — the `estimate` group of the existing global
 * settings, on its own screen, for admins.
 *
 * A separate group from the forecast's `analogs` on purpose: the two models match on different
 * things and must never inherit each other's tuning. This screen writes through the same
 * `PATCH /settings` every other setting uses, so it inherits the validation and the audit row
 * and adds neither.
 *
 * Two decisions about what the screen shows:
 *
 * **The three a person actually changes are at the top.** The two ceilings are what a planner
 * bumps into in the editor; the recency half-life is the one dial that changes what the model
 * thinks is relevant. Everything else is the shape of the similarity function, which wants
 * changing about once, by someone who has read Section 7.
 *
 * **The recency control states its meaning, not its coefficient.** `0.5 ^ (age / half_life)` is
 * the formula; "a project this many months old counts half as much as one finished today" is
 * what it means, and it is the sentence beside the input. Zero is not a degenerate case to be
 * guarded against — it is how recency is turned off — so the screen says that too.
 *
 * What the tool prices is not here. `pool_types` was drafted as a setting and is a constant
 * (POOL_TYPES), because a value that decides what every price is computed from should not be
 * widenable from a screen without the decision being taken again (12.4).
 */
class EstimateSettings extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _draft: { state: true },
    _error: { state: true },
    _saving: { state: true },
    _saved: { state: true },
    _problems: { state: true },
    _tuningOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._draft = null;
    this._error = null;
    this._saving = false;
    this._saved = false;
    this._problems = [];
    this._tuningOpen = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
      max-width: 780px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 18px;
      color: var(--ink-muted);
      font-size: 13.5px;
      max-width: 68ch;
    }
    .field {
      display: grid;
      gap: 4px;
      padding: 14px 0;
      border-top: 1px solid var(--border-soft);
    }
    .field:first-of-type {
      border-top: 0;
    }
    .field .row {
      display: flex;
      align-items: baseline;
      gap: 10px;
      flex-wrap: wrap;
    }
    label {
      font-size: 14px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    input {
      font: inherit;
      font-size: 14px;
      padding: 6px 8px;
      width: 8ch;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    input:disabled {
      opacity: 0.6;
    }
    .unit {
      font-size: 13px;
      color: var(--ink-muted);
    }
    .means {
      font-size: 12.5px;
      color: var(--ink-muted);
      max-width: 62ch;
      line-height: 1.5;
    }
    .means strong {
      color: var(--ink);
    }
    .default {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .default button {
      font: inherit;
      font-size: 12px;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      text-decoration: underline;
      cursor: pointer;
    }
    details {
      margin-top: 8px;
      border-top: 1px solid var(--border-soft);
      padding-top: 10px;
    }
    summary {
      cursor: pointer;
      font-size: 13px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    details .lede {
      margin: 10px 0 0;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 18px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    .actions button {
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 7px 16px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .actions button.primary {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }
    .actions button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .problems {
      margin: 14px 0 0;
      padding: 10px 14px;
      border: 1px solid var(--status-overloaded);
      border-radius: 6px;
      color: var(--status-overloaded);
      font-size: 13px;
    }
    .problems ul {
      margin: 6px 0 0;
      padding-left: 18px;
    }
    .ok {
      color: var(--status-healthy);
      font-weight: 600;
      font-size: 13px;
    }
    .state,
    .error {
      color: var(--ink-muted);
      padding: 16px 0;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  get _isAdmin() {
    return this.appState.user?.effective_role === 'admin';
  }

  async _load() {
    try {
      const data = await api.get('/settings');
      this._data = data;
      this._draft = {
        ...data.settings.estimate,
        similarity_weights: { ...data.settings.estimate.similarity_weights },
      };
      this._error = null;
    } catch (err) {
      this._error = `Could not load settings: ${err.message}`;
    }
  }

  _set(key, value) {
    this._draft = { ...this._draft, [key]: value };
    this._saved = false;
  }

  _setWeight(key, value) {
    this._draft = {
      ...this._draft,
      similarity_weights: { ...this._draft.similarity_weights, [key]: value },
    };
    this._saved = false;
  }

  get _changed() {
    return JSON.stringify(this._draft) !== JSON.stringify(this._data?.settings?.estimate);
  }

  /**
   * Save through the shared `PATCH /settings`. Its validation is the authority — this screen
   * does not second-guess it, it renders what it says. Nothing is stored when anything in the
   * group is invalid, so a rejected save leaves the draft on screen to be corrected.
   */
  async _save() {
    this._saving = true;
    this._problems = [];
    try {
      const body = { estimate: this._numeric(this._draft) };
      const data = await api.patch('/settings', body);
      this._data = data;
      this._draft = {
        ...data.settings.estimate,
        similarity_weights: { ...data.settings.estimate.similarity_weights },
      };
      this._saved = true;
    } catch (err) {
      this._problems = Array.isArray(err.details)
        ? err.details
        : [{ path: 'estimate', message: err.message }];
      this._saved = false;
    } finally {
      this._saving = false;
    }
  }

  /** Inputs hand back strings; the API wants numbers, and an empty box is not a zero. */
  _numeric(draft) {
    const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    return {
      ...draft,
      max_value_add: num(draft.max_value_add),
      max_principal_investment: num(draft.max_principal_investment),
      recency_half_life_months: num(draft.recency_half_life_months),
      similarity_exponent: num(draft.similarity_exponent),
      min_similarity: num(draft.min_similarity),
      min_count: num(draft.min_count),
      max_count: num(draft.max_count),
      ratings_stale_hours: num(draft.ratings_stale_hours),
      similarity_weights: {
        org_complexity: num(draft.similarity_weights?.org_complexity),
        human_factor: num(draft.similarity_weights?.human_factor),
      },
    };
  }

  _reset() {
    this._draft = {
      ...this._data.defaults.estimate,
      similarity_weights: { ...this._data.defaults.estimate.similarity_weights },
    };
    this._saved = false;
  }

  _field(key, { label, unit, means, step = '1', min = '0', max }) {
    const value = this._draft[key];
    const fallback = this._data.defaults.estimate[key];
    return html`
      <div class="field">
        <div class="row">
          <label for=${key}>${label}</label>
          <input
            id=${key}
            type="number"
            step=${step}
            min=${min}
            max=${max ?? nothing}
            .value=${String(value ?? '')}
            ?disabled=${!this._isAdmin || this._saving}
            @input=${(e) => this._set(key, e.target.value)}
          />
          ${unit ? html`<span class="unit">${unit}</span>` : nothing}
          ${
            Number(value) !== Number(fallback)
              ? html`<span class="default"
                  >default ${fallback} ·
                  <button @click=${() => this._set(key, fallback)}>restore</button></span
                >`
              : nothing
          }
        </div>
        <p class="means">${means}</p>
      </div>
    `;
  }

  /**
   * The recency control, which is the one that needs its meaning spelled out. The stored value
   * is a half-life in months; what a reader needs to know is what it does to a two-year-old
   * project, so the screen works that out in front of them.
   */
  _recency() {
    const months = Number(this._draft.recency_half_life_months);
    const off = months === 0;
    const weightAt = (age) => (off ? 1 : 0.5 ** (age / months));
    const pct = (n) => `${Math.round(n * 100)}%`;
    return html`
      <div class="field">
        <div class="row">
          <label for="recency_half_life_months">Recency half-life</label>
          <input
            id="recency_half_life_months"
            type="number"
            step="1"
            min="0"
            max="600"
            .value=${String(this._draft.recency_half_life_months ?? '')}
            ?disabled=${!this._isAdmin || this._saving}
            @input=${(e) => this._set('recency_half_life_months', e.target.value)}
          />
          <span class="unit">months</span>
          ${
            months !== Number(this._data.defaults.estimate.recency_half_life_months)
              ? html`<span class="default"
                  >default ${this._data.defaults.estimate.recency_half_life_months} ·
                  <button
                    @click=${() =>
                      this._set(
                        'recency_half_life_months',
                        this._data.defaults.estimate.recency_half_life_months,
                      )}
                  >
                    restore
                  </button></span
                >`
              : nothing
          }
        </div>
        <p class="means">
          ${
            off
              ? html`<strong>Recency is off.</strong> Every past project counts the same however old
                  it is, and only similarity decides the weight.`
              : html`A project <strong>${months} months</strong> old counts <strong>half</strong> as
                  much as one finished today. At that setting a one-year-old project carries
                  ${pct(weightAt(12))}, a three-year-old ${pct(weightAt(36))}, and a five-year-old
                  ${pct(weightAt(60))}. Set it to <strong>0</strong> to turn recency off entirely.`
          }
        </p>
      </div>
    `;
  }

  render() {
    if (this._error) return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._draft) return html`<div class="card"><p class="state">Loading…</p></div>`;

    const w = this._draft.similarity_weights ?? {};
    const sum = Number(w.org_complexity ?? 0) + Number(w.human_factor ?? 0);

    return html`
      <div class="card">
        <div class="eyebrow">Estimate settings</div>
        <h2>How a website is priced</h2>
        <p class="lede">
          The estimate matches a new website against past ones on two axes — the client's
          Organizational Complexity and Human Factor — and takes a weighted median of what they
          billed. These are the numbers that shape that. They are separate from the forecast's
          analog settings on purpose: the two models match on different things.
          ${
            this._isAdmin
              ? nothing
              : html`<strong>Changing them is an admin action; you are reading.</strong>`
          }
        </p>

        ${this._field('max_value_add', {
          label: 'Value Add ceiling',
          unit: '%',
          max: '100',
          means: html`The most an estimator may add to the base price for value the analogs do not
          capture. It caps the input on the estimate editor; it does not change any estimate already
          saved.`,
        })}
        ${this._field('max_principal_investment', {
          label: 'Principal Investment ceiling',
          unit: '%',
          max: '100',
          means: html`The most an estimator may discount for principal investment. Applied after
            Value Add, so the total is
            <code>base × (1 + value add) × (1 − principal investment)</code>.`,
        })}
        ${this._recency()}

        <details ?open=${this._tuningOpen} @toggle=${(e) => (this._tuningOpen = e.target.open)}>
          <summary>Model tuning</summary>
          <p class="lede">
            The shape of the similarity function. These want changing about once, by someone who has
            read Section 7 of the design — and then checking against the analog panel.
          </p>

          <div class="field">
            <div class="row">
              <label for="w_org">Weight: Organizational Complexity</label>
              <input
                id="w_org"
                type="number"
                step="0.05"
                min="0"
                max="1"
                .value=${String(w.org_complexity ?? '')}
                ?disabled=${!this._isAdmin || this._saving}
                @input=${(e) => this._setWeight('org_complexity', e.target.value)}
              />
              <label for="w_hf">Human Factor</label>
              <input
                id="w_hf"
                type="number"
                step="0.05"
                min="0"
                max="1"
                .value=${String(w.human_factor ?? '')}
                ?disabled=${!this._isAdmin || this._saving}
                @input=${(e) => this._setWeight('human_factor', e.target.value)}
              />
              <span class="unit ${Math.abs(sum - 1) > 0.001 ? 'bad' : ''}"
                >sum ${Math.round(sum * 100) / 100}</span
              >
            </div>
            <p class="means">
              How the two axes split the distance between a new estimate and a past project. They
              must sum to 1; the save is refused otherwise and nothing is stored.
            </p>
          </div>

          ${this._field('similarity_exponent', {
            label: 'Similarity exponent',
            step: '0.5',
            max: '10',
            means: html`How sharply a close match is preferred. At 2 an analog scoring 0.9 carries
            four times the weight of one scoring 0.45, rather than twice.`,
          })}
          ${this._field('min_similarity', {
            label: 'Similarity floor',
            step: '0.05',
            max: '1',
            means: html`How close a past project has to be to count at all. The floor is lifted
            automatically when fewer than the minimum survive it, and the estimate says so when that
            happens.`,
          })}
          ${this._field('min_count', {
            label: 'Analogs required',
            max: '1000',
            min: '1',
            means: html`Below this many surviving analogs the similarity floor is lifted rather than
            the estimate being refused.`,
          })}
          ${this._field('max_count', {
            label: 'Analogs used',
            max: '1000',
            min: '1',
            means: html`The most analogs a single price is computed from, closest first. Must not be
            below the number required.`,
          })}
          ${this._field('ratings_stale_hours', {
            label: 'Ratings cache',
            unit: 'hours',
            max: '8760',
            means: html`How old a client's cached ratings may be before the sync re-reads them from
            Workamajig. 67 clients, about seven seconds.`,
          })}
        </details>

        ${
          this._problems.length
            ? html`<div class="problems" role="alert">
                Nothing was saved:
                <ul>
                  ${this._problems.map(
                    (p) => html`<li>${p.path ? `${p.path}: ` : ''}${p.message}</li>`,
                  )}
                </ul>
              </div>`
            : nothing
        }
        ${
          this._isAdmin
            ? html`<div class="actions">
                <button
                  class="primary"
                  ?disabled=${!this._changed || this._saving}
                  @click=${() => this._save()}
                >
                  ${this._saving ? 'Saving…' : 'Save'}
                </button>
                <button ?disabled=${this._saving} @click=${() => this._reset()}>
                  Restore defaults
                </button>
                ${this._saved ? html`<span class="ok">Saved.</span>` : nothing}
              </div>`
            : nothing
        }
      </div>
    `;
  }
}

customElements.define('estimate-settings', EstimateSettings);
